import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X, Globe, ChevronDown, LogIn, LogOut, User } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Composant Robot Souriant
const SmilingRobot = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="4" width="16" height="16" rx="4" fill="currentColor" opacity="0.2"/>
    <rect x="4" y="4" width="16" height="16" rx="4" stroke="currentColor" strokeWidth="2"/>
    <circle cx="9" cy="10" r="1.5" fill="currentColor"/>
    <circle cx="15" cy="10" r="1.5" fill="currentColor"/>
    <path d="M8 14.5C8 14.5 9.5 17 12 17C14.5 17 16 14.5 16 14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <rect x="10" y="1" width="4" height="3" rx="1" fill="currentColor"/>
  </svg>
);

export const Header = () => {
  const { t, language, changeLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  // Rubriques Formation/PME/Freelance/Employes masquables depuis l'admin
  // (Reglages du site). Etat initial = memes valeurs par defaut que le
  // backend, pour que le menu s'affiche correctement des le premier rendu
  // sans "sauter" une fois la vraie reponse arrivee.
  const [pageVisibility, setPageVisibility] = useState({
    visible_formation: false, visible_pme: false, visible_freelance: false,
    visible_employes: false, visible_siteweb: true, visible_testimonials: true,
  });

  useEffect(() => {
    const token = localStorage.getItem('tannou_token');
    const userData = localStorage.getItem('tannou_user');
    if (token && userData) {
      setIsLoggedIn(true);
      setUser(JSON.parse(userData));
    }
  }, [location]);

  useEffect(() => {
    axios.get(`${API}/settings`).then((res) => setPageVisibility(res.data)).catch(() => {});
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('tannou_token');
    localStorage.removeItem('tannou_user');
    setIsLoggedIn(false);
    setUser(null);
    navigate('/');
  };

  const navItems = [
    { path: '/', label: t('home') },
    { path: '/formation', label: t('formation'), visible: pageVisibility.visible_formation },
    { path: '/pme', label: t('pme'), visible: pageVisibility.visible_pme },
    { path: '/freelance', label: t('freelance'), visible: pageVisibility.visible_freelance },
    { path: '/employes', label: t('employes'), visible: pageVisibility.visible_employes },
    { path: '/siteweb', label: 'Site Web', visible: pageVisibility.visible_siteweb },
    { path: '/testimonials', label: t('testimonials'), visible: pageVisibility.visible_testimonials },
  ].filter((item) => item.visible !== false);

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-200/50" data-testid="header">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo avec Robot Souriant */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-105 transition-all">
              <SmilingRobot className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-bold text-xl text-slate-900">AGENTORY</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  isActive(item.path)
                    ? 'bg-primary text-white'
                    : 'text-slate-600 hover:text-primary hover:bg-primary/5'
                }`}
                data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2" data-testid="language-switcher">
                  <Globe className="w-4 h-4" />
                  <span className="uppercase text-xs font-semibold">{language}</span>
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => changeLanguage('fr')} data-testid="lang-fr">
                  🇫🇷 Français
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => changeLanguage('en')} data-testid="lang-en">
                  🇬🇧 English
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Login / User Menu */}
            {isLoggedIn ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2 rounded-full" data-testid="user-menu-btn">
                    <User className="w-4 h-4" />
                    <span className="hidden sm:inline text-xs">{user?.name || 'Admin'}</span>
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <Link to="/admin" className="w-full cursor-pointer" data-testid="admin-link">
                      Tableau de bord
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/admin?tab=accounting" className="w-full cursor-pointer" data-testid="accounting-link">
                      Comptabilité
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login">
                <Button variant="outline" size="sm" className="gap-2 rounded-full" data-testid="login-btn">
                  <LogIn className="w-4 h-4" />
                  <span className="hidden sm:inline">Connexion</span>
                </Button>
              </Link>
            )}

            {/* CTA Button */}
            <Link to="/register">
              <Button className="hidden md:flex bg-primary hover:bg-primary-hover text-white rounded-full px-6 hover-lift" data-testid="cta-register">
                {t('getStarted')}
              </Button>
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              className="lg:hidden p-2 text-slate-600 hover:text-primary"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-slate-100 shadow-lg" data-testid="mobile-menu">
          <nav className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  isActive(item.path)
                    ? 'bg-primary text-white'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`mobile-nav-${item.path.replace('/', '') || 'home'}`}
              >
                {item.label}
              </Link>
            ))}
            <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
              <Button className="w-full mt-2 bg-primary hover:bg-primary-hover text-white rounded-full" data-testid="mobile-cta-register">
                {t('getStarted')}
              </Button>
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};
